import React, { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import { ReactComponent as Building } from "bootstrap-icons/icons/building.svg";
import { toast } from "react-toastify";
import Spinner from "../../spinner/Spinner";
import ApiServices from "../../../api/ApiServices";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { handleError } from "../../../components/utils/AuthenticationCheck";

const AssignBranchToHeadOffice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const axios = AxiosApiCall();

  // Get branch data from location state
  const branchData = location.state?.branchData || null;

  const [formData, setFormData] = useState({
    head_office_id: branchData?.head_office?.id || "",
    branch_id: branchData?.id || "",
  });

  const [headOffices, setHeadOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch all head offices
  const fetchHeadOffices = async () => {
    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) {
        throw new Error("No access token found.");
      }

      const response = await axios.get(ApiServices.getAllHeadOffices, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const { status, message, data } = response.data;

      if (status === 200) {
        setHeadOffices(data || []);
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchData) {
      toast.error("No branch data found");
      navigate("/operations/branch/list");
      return;
    }
    fetchHeadOffices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Get selected head office details
  const selectedHeadOffice = headOffices.find(
    (office) => office.id == formData.head_office_id
  );

  const validate = () => {
    const newErrors = {};
    if (!formData.head_office_id) {
      newErrors.head_office_id = "Please select a head office";
    }
    if (!formData.branch_id) {
      newErrors.branch_id = "Branch ID is missing";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) {
        throw new Error("No access token found.");
      }

      const response = await axios.post(
        ApiServices.assignBranchToHeadOffice,
        {
          head_office_id: parseInt(formData.head_office_id, 10),
          branch_id: parseInt(formData.branch_id, 10),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const { status, message } = response.data;

      if (status === 200) {
        toast.success(message);
        navigate("/operations/branch/list");
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate("/operations/branch/list")}
            className="text-orange1 hover:text-orange-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              {branchData?.head_office ? "Reassign" : "Assign"} Branch to Head Office
            </h1>
            <p className="text-gray-500">
              {branchData?.name ? `Branch: ${branchData.name}` : "Loading..."}
            </p>
            {branchData?.head_office && (
              <p className="text-sm text-blue-600 mt-1">
                Currently assigned to: <span className="font-semibold">{branchData.head_office.name}</span>
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Head Office Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Head Office <span className="text-red-500">*</span>
                </label>
                <select
                  name="head_office_id"
                  value={formData.head_office_id}
                  onChange={handleChange}
                  className={`w-full border ${
                    errors.head_office_id ? "border-red-500" : "border-gray-300"
                  } rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1`}
                >
                  <option value="">Select Head Office</option>
                  {headOffices.map((office) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
                {errors.head_office_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.head_office_id}
                  </p>
                )}
              </div>

              {/* Branch Dropdown (Read-only/Selected) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch <span className="text-red-500">*</span>
                </label>
                <div className="w-full border border-gray-300 bg-gray-100 rounded px-4 py-2 flex items-center gap-2">
                  <Building className="w-5 h-5 text-darkGreen3" />
                  <span className="font-medium text-gray-900">
                    {branchData?.name || "N/A"}
                  </span>
                </div>
                {errors.branch_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.branch_id}</p>
                )}
              </div>
            </div>

            {/* Information Display Section */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Branch Details Display */}
              {branchData && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-5 h-5 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-700">
                      Branch Information
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-600 font-medium">Name:</span>
                      <span className="ml-2 text-gray-900">
                        {branchData.name || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Email:</span>
                      <span className="ml-2 text-gray-900">
                        {branchData.email || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Phone:</span>
                      <span className="ml-2 text-gray-900">
                        {branchData.phone_number || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Location:</span>
                      <span className="ml-2 text-gray-900">
                        {branchData.city && branchData.state
                          ? `${branchData.city}, ${branchData.state}`
                          : "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Address:</span>
                      <span className="ml-2 text-gray-900">
                        {branchData.address || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Head Office Details Display */}
              {selectedHeadOffice && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-5 h-5 text-green-600" />
                    <h3 className="text-sm font-semibold text-gray-700">
                      Head Office Information
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-600 font-medium">Name:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedHeadOffice.name || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Email:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedHeadOffice.email || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Phone:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedHeadOffice.phone_number || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Location:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedHeadOffice.city && selectedHeadOffice.state
                          ? `${selectedHeadOffice.city}, ${selectedHeadOffice.state}`
                          : "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 font-medium">Address:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedHeadOffice.address || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Placeholder when no head office selected */}
              {!selectedHeadOffice && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Building className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Select a head office to view details</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={() => navigate("/operations/branch/list")}
                className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition disabled:opacity-50"
              >
                {submitting ? "Assigning..." : "Assign to Head Office"}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default AssignBranchToHeadOffice;
